import { Typography, Grid, useTheme } from '@mui/material';
import { MANTY_COLORS } from 'business/constants';
import React from 'react';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import Flex from 'ui/Flex/Flex';

import { currencyFormatter, formatFloatValue } from 'helpers/formatHelper';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { RetrospectiveT } from '../../helpers/types';
import { RetrospectivePayComponentSalaryMassData } from '../helpers/retrospectiveData';
import { StyText, StyPrimaryText } from '../helpers/StyTypography';
import { formatPercentageNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/components/KeysIndicators/helpers/dataHelpers';
import { getEvolutionAmounts } from './helpers/dataHelpers';
import { useTranslation } from 'react-i18next';

export const SalaryMassEvolution = ({
    retrospective,
    data,
}: {
    retrospective: RetrospectiveT;
    data: RetrospectivePayComponentSalaryMassData;
}): React.ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const {
        levelPercentage,
        levelIndice,
        massPercentage,
        massIndice,
        reportIndice,
        reportAmount,
    } = getEvolutionAmounts({ data });
    return (
        <StyPaper
            sx={{
                height: '100%',
                padding: '1rem',
                marginBottom: '0.2rem',
            }}
        >
            <Grid container gap={2}>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyText>
                            {t(
                                'humanResources.retrospective.salaryMass.levelEffect',
                            )}
                        </StyText>
                        <Flex justifyContent="end" gap={2}>
                            <StyPrimaryText>
                                {formatFloatValue(levelIndice)}
                            </StyPrimaryText>
                            <StyPrimaryText>
                                {formatPercentageNumber(levelPercentage)}
                            </StyPrimaryText>
                            {levelPercentage > 0 ? (
                                <TrendingUpIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.red,
                                    }}
                                />
                            ) : (
                                <TrendingDownIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.green,
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyText>
                            {t(
                                'humanResources.retrospective.salaryMass.massEffect',
                            )}
                        </StyText>
                        <Flex justifyContent="end" gap={2}>
                            <StyPrimaryText>
                                {formatFloatValue(massIndice)}
                            </StyPrimaryText>
                            <StyPrimaryText>
                                {formatPercentageNumber(massPercentage)}
                            </StyPrimaryText>
                            {massPercentage > 0 ? (
                                <TrendingUpIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.red,
                                    }}
                                />
                            ) : (
                                <TrendingDownIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.green,
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex justifyContent="space-between">
                        <StyText>
                            {t(
                                'humanResources.retrospective.salaryMass.reportEffect',
                                {
                                    year: retrospective.exercice + 1,
                                },
                            )}
                        </StyText>
                        <StyPrimaryText>
                            {reportIndice.toFixed(2)}
                        </StyPrimaryText>
                    </Flex>
                </Grid>
                <Grid item xs={12}>
                    <Flex
                        justifyContent="space-between"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: MANTY_COLORS.white,
                            padding: '0.5 rem 1rem',
                            borderRadius: '0.5rem',
                        }}
                    >
                        <StyText
                            sx={{
                                color: MANTY_COLORS.white,
                                padding: '0.5rem',
                            }}
                        >
                            {t(
                                'humanResources.retrospective.salaryMass.reportAmount',
                                {
                                    year: retrospective.exercice + 1,
                                },
                            )}
                        </StyText>
                        <Typography
                            sx={{
                                padding: '0.5rem',
                                color: MANTY_COLORS.white,
                            }}
                        >
                            {currencyFormatter(reportAmount)}
                        </Typography>
                    </Flex>
                </Grid>
            </Grid>
        </StyPaper>
    );
};
